import { useMemo, useState, useEffect } from 'react';

import { GlobalStyles } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeOptions, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import { loadFontForLanguage } from 'src/utils/font-loader';

import i18n from 'src/i18n';

import { SettingsValueProps, useSettingsContext } from 'src/components/settings';

import { palette } from './palette';
import { shadows } from './shadows';
import RTL from './options/right-to-left';
import { extraColor } from './extraColors';
import { createTypography } from './typography';
import { customShadows } from './custom-shadows';
import { componentsOverrides } from './overrides';
import { createPresets } from './options/presets';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
  themeMode?: SettingsValueProps['themeMode'];
  preserveState?: boolean; // 학습창에서 상태 보존을 위한 prop
};

export default function ThemeProvider({
  children,
  themeMode,
  preserveState = false, // 기본값은 false (기존 동작 유지)
}: Readonly<Props>) {
  const settings = useSettingsContext();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  const presets = createPresets(settings.themeColorPresets);
  const mode = themeMode ?? settings.themeMode;

  // 언어 변경 감지 및 폰트 동적 로딩
  useEffect(() => {
    const handleLanguageChange = async (lng: string) => {
      if (lng !== currentLanguage) {
        await loadFontForLanguage(lng);
        setCurrentLanguage(lng);
      }
    };

    // 초기 폰트 로드
    loadFontForLanguage(i18n.language);

    // i18n 언어 변경 이벤트 리스너
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [currentLanguage]);

  const memoizedValue = useMemo(
    () => ({
      palette: {
        ...palette(mode),
        ...presets.palette,
      },
      customShadows: {
        ...customShadows(mode),
        ...presets.customShadows,
      },
      typography: createTypography(currentLanguage),
      direction: settings.themeDirection,
      shadows: shadows(mode),
      shape: { borderRadius: 8 },

      extraColors: extraColor,
    }),
    [mode, settings.themeDirection, presets.palette, presets.customShadows, currentLanguage]
  );

  const theme = createTheme(memoizedValue as ThemeOptions);

  theme.components = componentsOverrides(theme);

  const className = `${mode}-theme`;

  const globalStyles = (
    <GlobalStyles
      styles={{
        ':root': {
          // Primary
          '--primary-lighter': theme.palette.primary.lighter,
          '--primary-light': theme.palette.primary.light,
          '--primary-main': theme.palette.primary.main,
          '--primary-dark': theme.palette.primary.dark,
          '--primary-darker': theme.palette.primary.darker,
          '--primary-contrastText': theme.palette.primary.contrastText,
          '--primary-8': theme.palette.primary['8'],
          '--primary-12': theme.palette.primary['12'],
          '--primary-16': theme.palette.primary['16'],
          '--primary-20': theme.palette.primary['20'],
          '--primary-24': theme.palette.primary['24'],
          '--primary-32': theme.palette.primary['32'],
          '--primary-48': theme.palette.primary['48'],

          // Secondary
          '--secondary-lighter': theme.palette.secondary.lighter,
          '--secondary-light': theme.palette.secondary.light,
          '--secondary-main': theme.palette.secondary.main,
          '--secondary-dark': theme.palette.secondary.dark,
          '--secondary-darker': theme.palette.secondary.darker,
          '--secondary-contrastText': theme.palette.secondary.contrastText,
          '--secondary-8': theme.palette.secondary['8'],
          '--secondary-12': theme.palette.secondary['12'],
          '--secondary-16': theme.palette.secondary['16'],
          '--secondary-20': theme.palette.secondary['20'],
          '--secondary-24': theme.palette.secondary['24'],
          '--secondary-32': theme.palette.secondary['32'],
          '--secondary-48': theme.palette.secondary['48'],

          // Info
          '--info-lighter': theme.palette.info.lighter,
          '--info-light': theme.palette.info.light,
          '--info-main': theme.palette.info.main,
          '--info-dark': theme.palette.info.dark,
          '--info-darker': theme.palette.info.darker,
          '--info-contrastText': theme.palette.info.contrastText,
          '--info-8': theme.palette.info['8'],
          '--info-12': theme.palette.info['12'],
          '--info-16': theme.palette.info['16'],
          '--info-20': theme.palette.info['20'],
          '--info-24': theme.palette.info['24'],
          '--info-32': theme.palette.info['32'],
          '--info-48': theme.palette.info['48'],

          // Success
          '--success-lighter': theme.palette.success.lighter,
          '--success-light': theme.palette.success.light,
          '--success-main': theme.palette.success.main,
          '--success-dark': theme.palette.success.dark,
          '--success-darker': theme.palette.success.darker,
          '--success-contrastText': theme.palette.success.contrastText,
          '--success-8': theme.palette.success['8'],
          '--success-12': theme.palette.success['12'],
          '--success-16': theme.palette.success['16'],
          '--success-20': theme.palette.success['20'],
          '--success-24': theme.palette.success['24'],
          '--success-32': theme.palette.success['32'],
          '--success-48': theme.palette.success['48'],

          // Warning
          '--warning-lighter': theme.palette.warning.lighter,
          '--warning-light': theme.palette.warning.light,
          '--warning-main': theme.palette.warning.main,
          '--warning-dark': theme.palette.warning.dark,
          '--warning-darker': theme.palette.warning.darker,
          '--warning-contrastText': theme.palette.warning.contrastText,
          '--warning-8': theme.palette.warning['8'],
          '--warning-12': theme.palette.warning['12'],
          '--warning-16': theme.palette.warning['16'],
          '--warning-20': theme.palette.warning['20'],
          '--warning-24': theme.palette.warning['24'],
          '--warning-32': theme.palette.warning['32'],
          '--warning-48': theme.palette.warning['48'],

          // Error
          '--error-lighter': theme.palette.error.lighter,
          '--error-light': theme.palette.error.light,
          '--error-main': theme.palette.error.main,
          '--error-dark': theme.palette.error.dark,
          '--error-darker': theme.palette.error.darker,
          '--error-contrastText': theme.palette.error.contrastText,
          '--error-8': theme.palette.error['8'],
          '--error-12': theme.palette.error['12'],
          '--error-16': theme.palette.error['16'],
          '--error-20': theme.palette.error['20'],
          '--error-24': theme.palette.error['24'],
          '--error-32': theme.palette.error['32'],
          '--error-48': theme.palette.error['48'],

          // Grey
          '--grey-100': theme.palette.grey[100],
          '--grey-200': theme.palette.grey[200],
          '--grey-300': theme.palette.grey[300],
          '--grey-400': theme.palette.grey[400],
          '--grey-500': theme.palette.grey[500],
          '--grey-600': theme.palette.grey[600],
          '--grey-700': theme.palette.grey[700],
          '--grey-800': theme.palette.grey[800],
          '--grey-900': theme.palette.grey[900],
          '--grey-8': theme.palette.grey['8'],
          '--grey-12': theme.palette.grey['12'],
          '--grey-16': theme.palette.grey['16'],
          '--grey-20': theme.palette.grey['20'],
          '--grey-24': theme.palette.grey['24'],
          '--grey-32': theme.palette.grey['32'],
          '--grey-48': theme.palette.grey['48'],
          '--grey-56': theme.palette.grey['56'],
          '--grey-64': theme.palette.grey['64'],
          '--grey-72': theme.palette.grey['72'],
          '--grey-80': theme.palette.grey['80'],

          // Common
          '--black': theme.palette.common.black,
          '--white': theme.palette.common.white,

          // Black
          '--black-main': theme.palette.black.main,
          '--black-8': theme.palette.black['8'],
          '--black-12': theme.palette.black['12'],
          '--black-16': theme.palette.black['16'],
          '--black-20': theme.palette.black['20'],
          '--black-24': theme.palette.black['24'],
          '--black-32': theme.palette.black['32'],
          '--black-48': theme.palette.black['48'],
          '--black-56': theme.palette.black['56'],
          '--black-64': theme.palette.black['64'],
          '--black-72': theme.palette.black['72'],
          '--black-80': theme.palette.black['80'],

          // White
          '--white-main': theme.palette.white.main,
          '--white-8': theme.palette.white['8'],
          '--white-12': theme.palette.white['12'],
          '--white-16': theme.palette.white['16'],
          '--white-20': theme.palette.white['20'],
          '--white-24': theme.palette.white['24'],
          '--white-32': theme.palette.white['32'],
          '--white-48': theme.palette.white['48'],
          '--white-56': theme.palette.white['56'],
          '--white-64': theme.palette.white['64'],
          '--white-72': theme.palette.white['72'],
          '--white-80': theme.palette.white['80'],

          // Shadows
          '--shadow-z1': theme.customShadows.z1,
          '--shadow-z4': theme.customShadows.z4,
          '--shadow-z8': theme.customShadows.z8,
          '--shadow-z12': theme.customShadows.z12,
          '--shadow-z16': theme.customShadows.z16,
          '--shadow-z20': theme.customShadows.z20,
          '--shadow-z24': theme.customShadows.z24,
          '--shadow-primary': theme.customShadows.primary,
          '--shadow-info': theme.customShadows.info,
          '--shadow-secondary': theme.customShadows.secondary,
          '--shadow-success': theme.customShadows.success,
          '--shadow-warning': theme.customShadows.warning,
          '--shadow-error': theme.customShadows.error,
          '--shadow-card': theme.customShadows.card,
          '--shadow-dialog': theme.customShadows.dialog,
          '--shadow-dropdown': theme.customShadows.dropdown,

          '--docs-layout-content-bg': theme.extraColors[mode].docsLayoutContentBg,
          '--docs-layout-header-bg': theme.extraColors[mode].docsLayoutHeaderBg,
          '--docs-components-color': theme.extraColors[mode].docsComponentsColor,
          '--docs-section-bg': theme.extraColors[mode].docsSectionBg,

          '--action-active': theme.extraColors[mode].actionActive,
          '--action-hover': theme.extraColors[mode].actionHover,
          '--action-selected': theme.extraColors[mode].actionSelected,
          '--action-focus': theme.extraColors[mode].actionFocus,
          '--action-disabled': theme.extraColors[mode].actionDisabled,
          '--action-disabled-bg': theme.extraColors[mode].actionDisabledBg,

          '--background-default': theme.extraColors[mode].backgroundDefault,
          '--background-paper': theme.extraColors[mode].backgroundPaper,
          '--background-neutral': theme.extraColors[mode].backgroundNeutral,

          '--components-divider': theme.extraColors[mode].componentsDivider,
          '--components-backdrop': theme.extraColors[mode].componentsBackdrop,
          '--components-button-outlined': theme.extraColors[mode].componentsButtonOutlined,
          '--components-paper-outlined': theme.extraColors[mode].componentsPaperOutlined,
          '--components-paper-bg-blur': theme.extraColors[mode].componentsPaperBgBlur,
          '--components-textfield-filled': theme.extraColors[mode].componentsTextfieldFilled,
          '--components-textfield-underline': theme.extraColors[mode].componentsTextfieldUnderline,
          '--components-textfield-outline': theme.extraColors[mode].componentsTextfieldOutline,
          '--components-tooltip-color': theme.extraColors[mode].componentsTooltipColor,
          '--components-tooltip-bg': theme.extraColors[mode].componentsTooltipBg,

          '--dark-light-primary': theme.extraColors[mode].darkLightPrimary,
          '--dark-light-secondary': theme.extraColors[mode].darkLightSecondary,
          '--dark-light-info': theme.extraColors[mode].darkLightInfo,
          '--dark-light-success': theme.extraColors[mode].darkLightSuccess,
          '--dark-light-warning': theme.extraColors[mode].darkLightWarning,
          '--dark-light-error': theme.extraColors[mode].darkLightError,

          '--darker-lighter-primary': theme.extraColors[mode].darkerLighterPrimary,
          '--darker-lighter-secondary': theme.extraColors[mode].darkerLighterSecondary,
          '--darker-lighter-info': theme.extraColors[mode].darkerLighterInfo,
          '--darker-lighter-success': theme.extraColors[mode].darkerLighterSuccess,
          '--darker-lighter-warning': theme.extraColors[mode].darkerLighterWarning,
          '--darker-lighter-error': theme.extraColors[mode].darkerLighterError,

          '--global-default-color': theme.extraColors[mode].globalDefaultColor,
          '--global-default-bg': theme.extraColors[mode].globalDefaultBg,
          '--global-default-bg-hovered': theme.extraColors[mode].globalDefaultBgHovered,

          '--global-inherit-color': theme.extraColors[mode].globalInheritColor,
          '--global-inherit-bg': theme.extraColors[mode].globalInheritBg,
          '--global-inherit-bg-hovered': theme.extraColors[mode].globalInheritBgHovered,

          '--lighter-darker-primary': theme.extraColors[mode].lighterDarkerPrimary,
          '--lighter-darker-secondary': theme.extraColors[mode].lighterDarkerSecondary,
          '--lighter-darker-info': theme.extraColors[mode].lighterDarkerInfo,
          '--lighter-darker-success': theme.extraColors[mode].lighterDarkerSuccess,
          '--lighter-darker-warning': theme.extraColors[mode].lighterDarkerWarning,
          '--lighter-darker-error': theme.extraColors[mode].lighterDarkerError,

          '--main-light-primary': theme.extraColors[mode].mainLightPrimary,
          '--main-light-secondary': theme.extraColors[mode].mainLightSecondary,
          '--main-light-info': theme.extraColors[mode].mainLightInfo,
          '--main-light-success': theme.extraColors[mode].mainLightSuccess,
          '--main-light-warning': theme.extraColors[mode].mainLightWarning,
          '--main-light-error': theme.extraColors[mode].mainLightError,

          '--text-primary': theme.extraColors[mode].textPrimary,
          '--text-secondary': theme.extraColors[mode].textSecondary,
          '--text-disabled': theme.extraColors[mode].textDisabled,
        },
      }}
    />
  );

  useEffect(() => {
    document.body.classList.remove('light-theme', 'dark-theme');
    document.body.classList.add(`${mode}-theme`);

    document.documentElement.classList.remove('light-theme', 'dark-theme');
    document.documentElement.classList.add(`${mode}-theme`);

    // 강제 리플로우 + 리페인트 유도 (보조 안전 장치)
    document.documentElement.style.display = 'none';
    requestAnimationFrame(() => {
      document.documentElement.style.display = '';
    });
  }, [mode, settings.themeMode]);

  return (
    // preserveState가 true일 때는 key를 제거하여 리마운트 방지 (학습창 진행 상태 보존)
    <MuiThemeProvider theme={theme} key={preserveState ? undefined : `${mode}-theme`}>
      <RTL themeDirection={settings.themeDirection}>
        <CssBaseline />
        {globalStyles}
        <div className={className}>{children}</div>
      </RTL>
    </MuiThemeProvider>
  );
}
